import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import type { RecorderResult } from '@/sections/asset/components/tabs/credentials/RecorderProgress/buildTimeline';
import {
  type RecorderLeadingStep,
  RecorderProgress,
} from '@/sections/asset/components/tabs/credentials/RecorderProgress/RecorderProgress';

// Controls whether the close button is reachable before the recorder reaches a
// terminal state. `always-enabled` lets the caller bail out mid-run (button
// reads "Stop watching" until finalize lands); `gate-until-terminal` disables
// the button until the recorder finishes.
export type RecorderClosePolicy = 'always-enabled' | 'gate-until-terminal';

export interface RecorderHostProps {
  conversationId: string;
  label: string;
  onComplete?: (result: RecorderResult) => void;
  closePolicy: RecorderClosePolicy; // required, explicit at every call site
  onClose: () => void;
  // Optional rows pinned to the top of the recorder's scrolling list. Forwarded
  // straight through to RecorderProgress. Host A omits it → unchanged.
  leadingSteps?: RecorderLeadingStep[];
}

// Presentational wrapper over RecorderProgress. RecorderProgress owns its own
// 3s `/my` polling and fires onComplete when the agent finalizes — RecorderHost
// adds NO polling. It only tracks the terminal result so it can render the
// close button (gating/label) and forward completion to the caller.
export const RecorderHost: React.FC<RecorderHostProps> = ({
  conversationId,
  label,
  onComplete,
  closePolicy,
  onClose,
  leadingSteps,
}) => {
  const [result, setResult] = useState<RecorderResult | null>(null);
  const handleComplete = (r: RecorderResult) => {
    setResult(r);
    onComplete?.(r);
  };
  const isTerminal = result !== null;
  const closeDisabled = closePolicy === 'gate-until-terminal' && !isTerminal;
  const closeLabel =
    closePolicy === 'always-enabled'
      ? isTerminal
        ? 'Close'
        : 'Stop watching'
      : 'Close';
  return (
    <div className="space-y-4">
      <RecorderProgress
        conversationId={conversationId}
        label={label}
        onComplete={handleComplete}
        leadingSteps={leadingSteps}
      />
      <div className="flex justify-end">
        <Button
          onClick={onClose}
          size="lg"
          disabled={closeDisabled}
          variant={isTerminal ? 'default' : 'ghost'}
          data-testid="recorder-close-button"
        >
          {closeLabel}
        </Button>
      </div>
    </div>
  );
};
